import { useAuth } from '@workos-inc/authkit-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import { User, Eye, EyeOff, CheckCircle2, X, CreditCard, ArrowUpRight } from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { InstructionOverlays } from '@/components/instructions/instruction-overlays'
import { useSubscription, useSubscriptionUsage, useCreatePortal } from '@/api'

function formatTierName(tier: string): string {
  return tier.charAt(0) + tier.slice(1).toLowerCase()
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function SettingsPage() {
  const { user } = useAuth()

  const { data: subscription, isLoading: subscriptionLoading } = useSubscription()
  const { data: usage, isLoading: usageLoading } = useSubscriptionUsage()
  const createPortal = useCreatePortal()

  const handleManageSubscription = async () => {
    try {
      const result = await createPortal.mutateAsync({
        returnUrl: window.location.href,
      })
      window.location.href = result.portal_url
    } catch (error) {
      console.error('Failed to open billing portal:', error)
    }
  }

  const displayName = user?.firstName && user?.lastName 
    ? `${user.firstName} ${user.lastName}`.trim()
    : null
  
  const initials = user?.firstName && user?.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`.toUpperCase()
    : user?.email?.[0]?.toUpperCase() || '?'

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h1 className="text-2xl font-bold tracking-tight lg:text-3xl">Settings</h1>
        <p className="text-muted-foreground">
          Manage your account and billing
        </p>
      </div>

      {/* Profile Section */}
      <Card className="overflow-hidden pt-0">
        <CardHeader className="border-b bg-muted/30 pt-6">
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Profile
          </CardTitle>
          <CardDescription>
            Your account information managed by WorkOS
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20 ring-4 ring-primary/10">
              <AvatarImage src={user?.profilePictureUrl || undefined} />
              <AvatarFallback className="bg-primary/10 text-primary text-xl font-semibold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-semibold">{displayName || 'User'}</h3>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
              <Badge variant="outline" className="mt-2 gap-1">
                <CheckCircle2 className="h-3 w-3 text-green-500" />
                Verified
              </Badge>
            </div>
          </div>
          <Separator />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={displayName || ''} disabled className="bg-muted/50" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user?.email || ''} disabled className="bg-muted/50" />
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Profile information is managed through WorkOS. Contact your administrator to make changes.
          </p>
        </CardContent>
      </Card>

      {/* Subscription & Billing Section */}
      <Card className="overflow-hidden pt-0">
        <CardHeader className="border-b bg-muted/30 pt-6">
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Subscription & Billing
          </CardTitle>
          <CardDescription>
            Manage your subscription and view usage
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          {subscriptionLoading || usageLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : subscription?.has_subscription && subscription.subscription ? (
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Current Plan</p>
                  <p className="text-2xl font-bold">{formatTierName(subscription.subscription.tier)}</p>
                </div>
                <Badge
                  variant={subscription.subscription.status === 'ACTIVE' ? 'default' : 'secondary'}
                >
                  {subscription.subscription.status === 'ACTIVE' ? 'Active' : subscription.subscription.status}
                </Badge>
              </div>

              {usage && (
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Emails this period</span>
                      <span className="font-medium">
                        {usage.stats.emails_used.toLocaleString()} / {usage.stats.emails_limit.toLocaleString()}
                      </span>
                    </div>
                    <Progress
                      value={usage.stats.usage_percentage}
                      className={`h-2 ${
                        usage.stats.usage_percentage >= 90
                          ? '[&>div]:bg-red-500'
                          : usage.stats.usage_percentage >= 70
                            ? '[&>div]:bg-yellow-500'
                            : ''
                      }`}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {usage.stats.emails_remaining.toLocaleString()} emails remaining
                      {usage.stats.days_remaining > 0 && ` · ${usage.stats.days_remaining} days left in period`}
                    </p>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Inboxes processing</span>
                      <span className="font-medium">
                        {usage.stats.inboxes_used} / {usage.stats.inboxes_limit}
                      </span>
                    </div>
                    <Progress
                      value={(usage.stats.inboxes_used / usage.stats.inboxes_limit) * 100}
                      className="h-2"
                    />
                  </div>

                  {usage.projection.will_exceed && (
                    <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-3 dark:bg-yellow-900/20 dark:border-yellow-800">
                      <p className="text-sm text-yellow-800 dark:text-yellow-200">
                        At your current rate, you may exceed your limit
                        {usage.projection.projected_exceed_date &&
                          ` around ${formatDate(usage.projection.projected_exceed_date)}`}
                        . Consider upgrading your plan.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="text-sm text-muted-foreground">
                <p>
                  Billing period: {formatDate(subscription.subscription.current_period_start)} -{' '}
                  {formatDate(subscription.subscription.current_period_end)}
                </p>
                {subscription.subscription.cancel_at_period_end && (
                  <p className="text-orange-600 dark:text-orange-400 mt-1">
                    Subscription will cancel at end of period
                  </p>
                )}
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={handleManageSubscription}
                  disabled={createPortal.isPending}
                >
                  {createPortal.isPending ? 'Loading...' : 'Manage Subscription'}
                </Button>
                <Button variant="ghost" asChild>
                  <Link to="/pricing" className="flex items-center gap-1">
                    Change Plan
                    <ArrowUpRight className="h-3 w-3" />
                  </Link>
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-6">
              <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <h3 className="text-lg font-semibold mb-1">No active subscription</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Subscribe to a plan to start processing emails with AI
              </p>
              <Button asChild>
                <Link to="/pricing">
                  View Plans
                  <ArrowUpRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <InstructionOverlays />
      {/* Data Privacy Section */}
      <Card className="overflow-hidden pt-0">
        <CardHeader className="border-b bg-muted/30 pt-6">
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            Data & Privacy
          </CardTitle>
          <CardDescription>
            How we handle your data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 pt-6">
          <div className="rounded-xl bg-primary/5 border border-primary/20 p-4">
            <div className="flex items-center gap-2 mb-2">
              <EyeOff className="h-4 w-4 text-primary" />
              <h4 className="font-semibold text-primary">Your email isn’t kept</h4>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Xero Inbox reads each email to decide what to do, then lets it go. We keep a
              record of what was done, not the email itself.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                What we store
              </h4>
              <ul className="list-none text-sm text-muted-foreground space-y-2.5">
                <li className="flex items-center gap-2.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  <span>Your account information (via WorkOS)</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  <span>Connected email account tokens (encrypted)</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  <span>Your instruction configurations</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  <span>What was done to each email (action, time, result)</span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                  <span>Setup chat conversations, including any email the assistant read for you (encrypted, deleted after 30 days without activity)</span>
                </li>
              </ul>
            </div>
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <EyeOff className="h-4 w-4 text-primary" />
                What processing never stores
              </h4>
              <ul className="list-none text-sm text-muted-foreground space-y-2.5">
                <li className="flex items-center gap-2.5">
                  <X className="h-3.5 w-3.5 text-destructive shrink-0" />
                  <span>Email content, subjects, or bodies</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <X className="h-3.5 w-3.5 text-destructive shrink-0" />
                  <span>Sender or recipient details</span>
                </li>
                <li className="flex items-center gap-2.5">
                  <X className="h-3.5 w-3.5 text-destructive shrink-0" />
                  <span>Attachments</span>
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
